'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import { Lock, ArrowLeft } from 'lucide-react'
import { useIsMobile } from '@/hooks/use-mobile'
import ModuleNoteTechnique from './transqual/module-note-technique'
import Module01 from './transqual/module-01'
import Module02 from './transqual/module-02'
import Module03 from './transqual/module-03'

interface UserProfile {
  id: string
  email: string
  full_name: string | null
  credits: number
  plan: string
}

interface ModulesSectionProps {
  profile: UserProfile | null
  /** Notifie le parent quand un module est ouvert en plein écran sur mobile
   *  (drill-in) → permet de masquer le Hero et les sections au-dessus. */
  onMobileViewChange?: (open: boolean) => void
}

// ── Palette ──────────────────────────────────────────────────────────────────
const N = {
  900: '#111',
  800: '#1e1e1e',
  700: '#2c2c2c',
  500: '#6b6b6b',
  400: '#6B7280',
  100: '#eeeeee',
  50:  '#f9f9f7',
}

// ── Données modules ───────────────────────────────────────────────────────────
const MODULES = [
  {
    num: '01',
    icon: '📋',
    color: '#2d6614',
    bg: '#f0f8e8',
    borderColor: '#d4edb8',
    title: 'Note Technique Qualitative',
    shortTitle: 'Note tech.',
    subtitle: 'Note technique qualitative prête à partir de votre TDR en quelques minutes',
    desc: 'Rédigez des propositions techniques qualitatives professionnelles à partir de vos TDR. Qualyse analyse le document et génère automatiquement une note technique d\'évaluation qualitative complète — cadre méthodologique, échantillonnage, outils de collecte, chronogramme, budget indicatif.',
    details: [
      { label: 'Entrée', value: 'TDR en PDF, DOCX ou TXT' },
      { label: 'Sortie', value: 'Note technique structurée' },
    ],
    steps: [
      { title: 'Téléversez votre TDR',              detail: 'PDF, DOCX ou TXT acceptés' },
      { title: 'Qualyse analyse le document',        detail: 'Objectifs, contexte, contraintes extraits' },
      { title: 'Qualyse génère la note technique',   detail: 'Méthodologie, outils, chronogramme' },
      { title: 'Téléchargez votre proposition',      detail: 'Format Word professionnel' },
    ],
    tags: ['Upload TDR', 'Méthodologie automatique', 'Échantillonnage', 'Chronogramme', 'Export DOCX'],
    useCase: 'Idéal pour : cabinets de conseil, évaluateurs, chercheurs répondant à des appels d\'offres.',
  },
  {
    num: '02',
    icon: '🎤',
    color: '#1a4a7a',
    bg: '#e8f0f8',
    borderColor: '#b5d4f4',
    title: 'Transcription & Correction des verbatims',
    shortTitle: 'Transcription',
    subtitle: null,
    desc: 'Transcription automatique de vos entretiens audio en français et dans 98 langues. Correction intelligente des verbatims avec contexte méthodologique — orthographe, ponctuation, structure des tours de parole, hésitations analytiquement significatives préservées.',
    details: [
      { label: 'Entrée',  value: 'Fichier audio ou vidéo' },
      { label: 'Sortie',  value: 'Verbatim corrigé et structuré' },
      { label: 'Formats', value: 'MP3 · MP4 · WAV · M4A · MOV' },
    ],
    steps: [
      { title: 'Déposez votre fichier audio ou vidéo', detail: 'MP3, MP4, WAV, M4A, MOV acceptés' },
      { title: 'Qualyse transcrit automatiquement',    detail: '98 langues, ponctuation incluse' },
      { title: 'Qualyse corrige le verbatim',          detail: 'Tours de parole, hésitations préservées' },
      { title: 'Téléchargez votre verbatim',           detail: 'Prêt pour l\'analyse ou la codification' },
    ],
    tags: ['Upload audio/vidéo', '98 langues', 'Correction automatique', 'Tours de parole', 'Export TXT et Word'],
    useCase: 'Idéal pour : chercheurs, doctorants, journalistes, évaluateurs terrain.',
  },
  {
    num: '03',
    icon: '🏷️',
    color: '#1a6060',
    bg: '#e8f5f5',
    borderColor: '#9fe1cb',
    title: 'Codification automatique des verbatims',
    shortTitle: 'Codification',
    subtitle: 'Codification automatique de vos verbatims comme NVivo ou MAXQDA',
    desc: 'Codification automatique de vos verbatims — alternative NVivo et MAXQDA sans licence. Analyse thématique de contenu avec approche inductive, déductive ou mixte. Citations représentatives, arbre de codes hiérarchisé, fréquences relatives et analyse comparative inter-entretiens.',
    details: [
      { label: 'Approches',  value: 'Inductive · Déductive · Mixte' },
      { label: 'Référence',  value: 'Braun & Clarke, Paillé & Mucchielli' },
      { label: 'Multi-docs', value: 'Comparaison inter-entretiens' },
    ],
    steps: [
      { title: 'Téléversez vos verbatims',       detail: '1 ou plusieurs documents texte' },
      { title: 'Choisissez votre approche',       detail: 'Inductive, déductive ou mixte' },
      { title: 'Qualyse codifie et extrait',      detail: 'Thèmes, citations, arbre de codes' },
      { title: 'Téléchargez la codification',     detail: 'À valider et ajuster selon votre posture' },
    ],
    tags: ['Multi-verbatims', 'Arbre de codes', 'Citations extraites', 'Inductive / Déductive', 'Export Excel'],
    useCase: 'Idéal pour : thèses, études terrain, évaluations de projets, recherches-actions.',
  },
  {
    num: '04',
    icon: '🧠',
    color: '#6a3500',
    bg: '#fdf3e8',
    borderColor: '#f5c4a0',
    title: 'Rapport d\'analyse qualitative',
    shortTitle: 'Rapport',
    subtitle: 'Génération de rapports d\'analyse qualitative selon 4 méthodes scientifiques',
    desc: 'Génération de rapports d\'analyse qualitative complets selon 4 méthodes scientifiques : analyse thématique de contenu, narrative, IPA (phénoménologie interprétative) et théorisation ancrée. Importez vos verbatims et obtenez un rapport qualitatif prêt à livrer.',
    details: [
      { label: 'Méthodes', value: 'Thématique-Contenu · Narrative · Comparative' },
      { label: 'Formats',  value: 'Rapport académique · Synthèse · Mémo' },
      { label: 'Codif',    value: 'Optionnelle — analyse directe possible' },
    ],
    steps: [
      { title: 'Importez vos données',         detail: 'Verbatims avec ou sans codes' },
      { title: 'Choisissez votre méthode',     detail: '3 types d\'analyse disponibles' },
      { title: 'Qualyse génère le rapport',    detail: 'Structuré, citable, prêt à livrer' },
      { title: 'Téléchargez le rapport',       detail: 'Format Word professionnel' },
    ],
    tags: ['3 méthodes d\'analyse', 'Sans codif obligatoire', 'Rapport académique', 'Note de synthèse', 'Export DOCX'],
    useCase: 'Idéal pour : rapports d\'évaluation, articles scientifiques, livrables de consulting.',
  },
]

// ── Données spécifiques aux nouvelles cartes (design "Modules Cards") ────────
// Court résumé + statistiques affichés sur la card. L'icône est une SVG fidèle
// au mockup HTML (Modules Cards Qualyse.html). Chaque card a son propre jeu
// de variables CSS (--qly-accent / --qly-soft / --qly-borderc / --qly-shadow)
// utilisées dans le style scopé .qly-mcard.
const CARDS: Array<{
  cardTitle: string
  cardDesc: string
  metaSecondary: string
  icon: React.ReactNode
  theme: { accent: string; soft: string; borderc: string; shadow: string }
}> = [
  {
    cardTitle: 'Note Technique',
    cardDesc: 'Votre proposition technique à partir du TDR',
    metaSecondary: 'Format DOCX',
    theme: { accent: '#2d6614', soft: '#f0f8e8', borderc: '#d4edb8', shadow: 'rgba(45,102,20,0.25)' },
    icon: (
      <svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <rect x="6" y="3" width="20" height="26" rx="3" stroke="#2d6614" strokeWidth="1.8" fill="white" />
        <rect x="11" y="1" width="10" height="4" rx="1.5" fill="#2d6614" />
        <path d="M11 11h10M11 15h10M11 19h7" stroke="#2d6614" strokeWidth="1.6" strokeLinecap="round" />
        <circle cx="22" cy="23" r="4" fill="#d4edb8" stroke="#2d6614" strokeWidth="1.5" />
        <path d="M20.5 23l1 1 2-2" stroke="#2d6614" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    cardTitle: 'Transcription',
    cardDesc: 'Vos entretiens audio en verbatim corrigé',
    metaSecondary: '98 langues',
    theme: { accent: '#1a4a7a', soft: '#e8f0f8', borderc: '#b5d4f4', shadow: 'rgba(26,74,122,0.25)' },
    icon: (
      <svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <rect x="12" y="4" width="8" height="14" rx="4" fill="#1a4a7a" />
        <path d="M8 14c0 5 3.6 9 8 9s8-4 8-9" stroke="#1a4a7a" strokeWidth="1.8" strokeLinecap="round" fill="none" />
        <line x1="16" y1="23" x2="16" y2="28" stroke="#1a4a7a" strokeWidth="1.8" strokeLinecap="round" />
        <line x1="11" y1="28" x2="21" y2="28" stroke="#1a4a7a" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M5 11c-1 1-1 3 0 4" stroke="#1a4a7a" strokeWidth="1.4" strokeLinecap="round" opacity="0.6" />
        <path d="M3 9c-2 2-2 6 0 8" stroke="#1a4a7a" strokeWidth="1.4" strokeLinecap="round" opacity="0.3" />
        <path d="M27 11c1 1 1 3 0 4" stroke="#1a4a7a" strokeWidth="1.4" strokeLinecap="round" opacity="0.6" />
        <path d="M29 9c2 2 2 6 0 8" stroke="#1a4a7a" strokeWidth="1.4" strokeLinecap="round" opacity="0.3" />
      </svg>
    ),
  },
  {
    cardTitle: 'Codification',
    cardDesc: 'Analyse thématique automatique, sans NVivo',
    metaSecondary: 'Multi-verbatims',
    theme: { accent: '#1a6060', soft: '#e8f5f5', borderc: '#9fe1cb', shadow: 'rgba(26,96,96,0.25)' },
    icon: (
      <svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <path d="M16 3l13 13-12.5 12.5a2 2 0 01-2.8 0L4 18.7V6a3 3 0 013-3h9z" fill="#9fe1cb" stroke="#1a6060" strokeWidth="1.8" strokeLinejoin="round" />
        <circle cx="11" cy="11" r="2.2" fill="#1a6060" />
        <path d="M19 14l5 5M16 18l4 4M14 21l3 3" stroke="#1a6060" strokeWidth="1.4" strokeLinecap="round" opacity="0.4" />
      </svg>
    ),
  },
  {
    cardTitle: 'Rapport d’analyse',
    cardDesc: 'Rapport qualitatif selon 4 méthodes scientifiques',
    metaSecondary: 'Export DOCX',
    theme: { accent: '#6a3500', soft: '#fdf3e8', borderc: '#f5c4a0', shadow: 'rgba(106,53,0,0.25)' },
    icon: (
      <svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <rect x="4" y="6" width="20" height="22" rx="2.5" fill="white" stroke="#6a3500" strokeWidth="1.8" />
        <path d="M8 11h12M8 14h8" stroke="#6a3500" strokeWidth="1.6" strokeLinecap="round" />
        <rect x="8" y="22" width="3" height="3" fill="#6a3500" opacity="0.5" />
        <rect x="12.5" y="19" width="3" height="6" fill="#6a3500" opacity="0.7" />
        <rect x="17" y="17" width="3" height="8" fill="#6a3500" />
        <circle cx="24" cy="9" r="5.5" fill="#6a3500" />
        <path d="M22 9l1.5 1.5L26.5 7.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
]

export default function ModulesSection({ profile: _, onMobileViewChange }: ModulesSectionProps) {
  const [active, setActive] = useState(0)
  const [hasResult, setHasResult] = useState(false)
  const m = MODULES[active]

  // ── Navigation mobile « drill-in » ──────────────────────────────────────
  // Sur mobile uniquement : taper une carte ouvre le module en plein écran
  // (cartes masquées) pour atterrir directement sur sa zone d'upload, sans
  // défiler parmi les autres. Desktop : comportement inchangé (mobileOpen
  // n'a aucun effet quand !isMobile). useIsMobile renvoie false au 1er rendu
  // puis se corrige après montage → on démarre toujours sur la liste.
  const isMobile = useIsMobile()
  const [mobileOpen, setMobileOpen] = useState(false)
  const drillRef = useRef<HTMLDivElement>(null)

  useEffect(() => { setHasResult(false) }, [active])
  const handleResult = useCallback((has: boolean) => setHasResult(has), [])

  // Mode immersif mobile : signale au parent qu'un module est ouvert en
  // plein écran (drill-in) → le Hero et les sections au-dessus sont masqués.
  const immersive = isMobile && mobileOpen
  useEffect(() => {
    onMobileViewChange?.(immersive)
  }, [immersive, onMobileViewChange])

  // À l'ouverture d'un module sur mobile, amène la vue sur le haut du module
  // (bouton retour + zone d'upload) plutôt que de laisser l'écran au milieu.
  useEffect(() => {
    if (isMobile && mobileOpen) {
      drillRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [isMobile, mobileOpen, active])

  const handleSelect = useCallback((i: number) => {
    setActive(i)
    setMobileOpen(true) // sans effet sur desktop (vue détail toujours affichée)
  }, [])

  return (
    <section
      id="comment-ca-marche"
      className="modules-section"
      style={{ padding: '80px 40px', background: '#fff', borderBottom: '1px solid #e8e4dc' }}
    >
      <style>{`
        @keyframes moduleFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: none; }
        }
        @keyframes qlyMCardBlink {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.3; }
        }
        @keyframes qlyMCardOrbit {
          to { transform: rotate(360deg); }
        }

        /* ── Cards des 4 modules — design "Modules Cards Qualyse" ── */
        .qly-mcards {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
          margin-bottom: 28px;
        }
        .qly-mcard {
          position: relative;
          background: white;
          border: 1.5px solid #ece8df;
          border-radius: 16px;
          padding: 16px 16px 14px;
          cursor: pointer;
          transition:
            transform 0.3s cubic-bezier(.4,1.5,.5,1),
            box-shadow 0.3s ease,
            border-color 0.2s ease,
            background 0.3s ease;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          gap: 12px;
          min-height: 0;
          text-align: left;
          font: inherit;
          color: inherit;
        }
        .qly-mcard:hover {
          transform: translateY(-6px);
          box-shadow: 0 18px 40px -12px rgba(0,0,0,0.14);
          border-color: var(--qly-accent);
        }
        .qly-mcard.is-active {
          border-color: var(--qly-accent);
          background: var(--qly-soft);
          box-shadow: 0 12px 36px -12px var(--qly-shadow);
        }
        .qly-mcard::before {
          content: '';
          position: absolute;
          top: -32px; right: -32px;
          width: 110px; height: 110px;
          border-radius: 50%;
          background: var(--qly-soft);
          opacity: 0.7;
          transition: transform 0.4s ease, opacity 0.3s, background 0.3s;
          z-index: 0;
          pointer-events: none;
        }
        .qly-mcard:hover::before { transform: scale(1.3); opacity: 1; }
        .qly-mcard.is-active::before {
          background: var(--qly-accent);
          opacity: 0.08;
          transform: scale(1.5);
        }
        .qly-mcard::after {
          content: '';
          position: absolute;
          bottom: 0; left: 0;
          height: 3px; width: 100%;
          background: var(--qly-accent);
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.4s ease;
          border-radius: 0 0 18px 18px;
        }
        .qly-mcard.is-active::after,
        .qly-mcard:hover::after { transform: scaleX(1); }

        .qly-mcard-top {
          display: flex; align-items: center; justify-content: space-between;
          position: relative; z-index: 1;
        }
        .qly-mcard-badge {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 4px 9px;
          background: var(--qly-soft);
          color: var(--qly-accent);
          border: 1px solid var(--qly-borderc);
          border-radius: 6px;
          font-size: 10px; font-weight: 800;
          font-family: monospace;
          letter-spacing: 0.08em;
        }
        .qly-mcard-badge-dot {
          width: 5px; height: 5px;
          border-radius: 50%;
          background: var(--qly-accent);
          animation: qlyMCardBlink 2s ease-in-out infinite;
        }
        .qly-mcard-arrow {
          width: 28px; height: 28px;
          border-radius: 8px;
          background: white;
          border: 1px solid #eee;
          display: flex; align-items: center; justify-content: center;
          transition: all 0.25s;
          color: #999;
        }
        .qly-mcard:hover .qly-mcard-arrow,
        .qly-mcard.is-active .qly-mcard-arrow {
          background: var(--qly-accent);
          border-color: var(--qly-accent);
          color: white;
          transform: rotate(-45deg);
        }
        .qly-mcard-icon {
          width: 52px; height: 52px;
          border-radius: 14px;
          background: var(--qly-soft);
          border: 1.5px solid var(--qly-borderc);
          display: flex; align-items: center; justify-content: center;
          position: relative; z-index: 1;
          transition: transform 0.4s cubic-bezier(.4,1.5,.5,1), box-shadow 0.3s;
        }
        .qly-mcard:hover .qly-mcard-icon,
        .qly-mcard.is-active .qly-mcard-icon {
          transform: scale(1.08) rotate(-3deg);
          box-shadow: 0 8px 20px -6px var(--qly-shadow);
        }
        .qly-mcard-icon svg { width: 26px; height: 26px; }
        .qly-mcard-icon::after {
          content: '';
          position: absolute;
          inset: -5px;
          border-radius: 18px;
          border: 1.5px dashed var(--qly-accent);
          opacity: 0;
          animation: qlyMCardOrbit 8s linear infinite;
          transition: opacity 0.3s;
          pointer-events: none;
        }
        .qly-mcard:hover .qly-mcard-icon::after,
        .qly-mcard.is-active .qly-mcard-icon::after { opacity: 0.35; }

        .qly-mcard-body {
          position: relative; z-index: 1;
          display: flex; flex-direction: column; gap: 4px;
          margin-top: auto;
        }
        .qly-mcard-title {
          font-family: 'DM Serif Display', Georgia, serif;
          font-size: 17px; font-weight: 800;
          color: #111;
          line-height: 1.2;
          letter-spacing: -0.4px;
          transition: color 0.2s ease;
        }
        .qly-mcard.is-active .qly-mcard-title { color: var(--qly-accent); }
        .qly-mcard-desc {
          font-size: 12px;
          line-height: 1.4;
          color: #6b6b6b;
          margin-top: 2px;
        }
        .qly-mcard-meta {
          position: relative; z-index: 1;
          display: flex; align-items: center; gap: 8px;
          padding-top: 8px;
          border-top: 1px dashed #ece8df;
          font-size: 10.5px;
          color: #6B7280;
          font-weight: 500;
        }
        .qly-mcard-meta strong { color: #1a1a1a; font-weight: 700; }
        .qly-mcard-meta-sep {
          width: 3px; height: 3px;
          background: #ccc;
          border-radius: 50%;
        }

        @media (max-width: 1100px) {
          .qly-mcards { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 900px) {
          .modules-grid { grid-template-columns: 1fr !important; }
          .modules-section { padding: 48px 20px !important; }
        }
        @media (max-width: 600px) {
          .qly-mcards { grid-template-columns: 1fr; }
        }
      `}</style>

      <div style={{ maxWidth: 1100, margin: '0 auto' }}>

        {/* ── En-tête (masqué en mode immersif mobile) ─────────────── */}
        {!immersive && (
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '5px 14px', background: '#f0f8e8', color: '#2d6614',
            border: '1px solid rgba(45,102,20,0.2)', borderRadius: 100,
            fontSize: 11, fontWeight: 700, letterSpacing: '0.07em',
            textTransform: 'uppercase', fontFamily: 'monospace',
          }}>
            4 modules indépendants
          </span>
          <h2 className="qly-modules-h2" style={{
            fontSize: 'clamp(22px,2.4vw,32px)', fontWeight: 800,
            marginTop: 16, marginBottom: 0,
            letterSpacing: '-0.4px', fontFamily: 'Georgia, serif',
            whiteSpace: 'nowrap',
          }}>
            <span style={{ color: '#2d6614' }}>Du TDR au rapport final&nbsp;</span>
            <span style={{ color: N[900] }}>en quelques minutes</span>
          </h2>
        </div>
        )}

        {/* ── Cartes des 4 modules ── (masquées en vue module mobile) ── */}
        {(!isMobile || !mobileOpen) && (
        <div id="tour-modules-nav" className="qly-mcards">
          {CARDS.map((card, i) => {
            const isActive = active === i
            const mod = MODULES[i]
            return (
              <button
                key={mod.num}
                id={`tour-module-0${i + 1}`}
                type="button"
                className={`qly-mcard${isActive ? ' is-active' : ''}`}
                onClick={() => handleSelect(i)}
                aria-pressed={isActive}
                style={{
                  // Variables CSS scopées à la carte — consommées par les
                  // sélecteurs .qly-mcard* dans le <style> ci-dessus.
                  ['--qly-accent' as string]: card.theme.accent,
                  ['--qly-soft' as string]: card.theme.soft,
                  ['--qly-borderc' as string]: card.theme.borderc,
                  ['--qly-shadow' as string]: card.theme.shadow,
                }}
              >
                <div className="qly-mcard-top">
                  <span className="qly-mcard-badge">
                    <span className="qly-mcard-badge-dot" />
                    MODULE {mod.num}
                  </span>
                  <span className="qly-mcard-arrow" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M3 11L11 3M11 3H5M11 3V9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </div>

                <div className="qly-mcard-icon">{card.icon}</div>

                <div className="qly-mcard-body">
                  <div className="qly-mcard-title">{card.cardTitle}</div>
                  <div className="qly-mcard-desc">{card.cardDesc}</div>
                </div>

                <div className="qly-mcard-meta">
                  <span>{card.metaSecondary}</span>
                </div>
              </button>
            )
          })}
        </div>
        )}

        {/* ── Bouton retour (mobile, vue module) ──────────────────── */}
        {isMobile && mobileOpen && (
          <div ref={drillRef} style={{ marginBottom: 16, scrollMarginTop: 80 }}>
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '9px 16px', borderRadius: 10,
                background: '#fff', border: `1.5px solid ${m.borderColor}`,
                color: m.color, fontSize: 14, fontWeight: 700, cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              <ArrowLeft size={16} /> Retour aux modules
            </button>
          </div>
        )}

        {/* ── Détail du module actif ──────────────────────────────── */}
        {(!isMobile || mobileOpen) && (
        <div
          key={active}
          className="modules-grid"
          style={{
            display: 'grid', gridTemplateColumns: '1fr', gap: 28,
            animation: 'moduleFadeIn 0.3s ease',
          }}
        >
          {/* GAUCHE — Description + Fonctionnalités + Étapes
              Masquée aussi en vue module mobile : on veut l'upload en premier. */}
          <div style={{ display: (hasResult || (isMobile && mobileOpen)) ? 'none' : 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
            {/* Carte principale : icône, titre, description */}
            <div style={{
              padding: 28, background: m.bg, borderRadius: 12,
              border: `1px solid ${m.borderColor}`,
              flex: 1,
            }}>
              {/* Icône + titre */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div style={{
                  width: 46, height: 46, background: '#fff', borderRadius: 10,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 24, border: `1px solid ${m.borderColor}`, flexShrink: 0,
                }}>
                  {m.icon}
                </div>
                <div>
                  <div style={{
                    fontSize: 10, color: m.color, fontWeight: 700,
                    fontFamily: 'monospace', letterSpacing: '0.08em',
                  }}>
                    MODULE {m.num}
                  </div>
                  <h3 style={{ fontSize: 18, fontWeight: 800, color: N[900], marginTop: 2, letterSpacing: '-0.3px' }}>
                    {m.title}
                  </h3>
                </div>
              </div>

              {m.subtitle && (
                <p style={{ fontSize: 12, color: m.color, fontWeight: 600, marginBottom: 10, fontStyle: 'italic' }}>
                  {m.subtitle}
                </p>
              )}

              <p style={{ fontSize: 13, color: N[700], lineHeight: 1.75, marginBottom: 0 }}>
                {m.desc}
              </p>
            </div>

            {active !== 0 && active !== 1 && active !== 2 && active !== 3 && <>
            {/* Fonctionnalités */}
            <div style={{ padding: '16px 20px', background: N[50], borderRadius: 12, border: `1px solid ${N[100]}` }}>
              <div style={{
                fontSize: 10, color: N[400], fontWeight: 700,
                fontFamily: 'monospace', letterSpacing: '0.06em', marginBottom: 10,
              }}>
                FONCTIONNALITÉS
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {m.tags.map(tag => (
                  <span key={tag} style={{
                    padding: '4px 10px', background: m.bg, color: m.color,
                    borderRadius: 4, fontSize: 11, fontWeight: 600,
                    border: `1px solid ${m.borderColor}`,
                  }}>
                    ✓ {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Étapes — 2 colonnes */}
            <div style={{ padding: '16px 20px', background: '#fff', borderRadius: 12, border: `1px solid ${N[100]}` }}>
              <div style={{
                fontSize: 10, color: N[400], fontWeight: 700,
                fontFamily: 'monospace', letterSpacing: '0.06em', marginBottom: 12,
              }}>
                COMMENT ÇA MARCHE
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' }}>
                {m.steps.map((step, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <div style={{
                      width: 20, height: 20, borderRadius: '50%',
                      background: m.bg, border: `1.5px solid ${m.color}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 9, fontWeight: 800, color: m.color,
                      flexShrink: 0, marginTop: 1,
                    }}>
                      {i + 1}
                    </div>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: N[800], marginBottom: 1 }}>
                        {step.title}
                      </div>
                      <div style={{ fontSize: 10, color: N[500] }}>
                        {step.detail}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Détails techniques + cas d'usage */}
            <div style={{
              padding: '14px 18px', background: '#fff', borderRadius: 10,
              border: `1px solid ${m.borderColor}`,
            }}>
              <div style={{
                fontSize: 10, color: m.color, fontWeight: 700,
                fontFamily: 'monospace', letterSpacing: '0.06em', marginBottom: 10,
              }}>
                DÉTAILS TECHNIQUES
              </div>
              {m.details.map(d => (
                <div key={d.label} style={{ display: 'flex', gap: 10, marginBottom: 6, alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 11, color: N[400], minWidth: 80, fontWeight: 600 }}>
                    {d.label}
                  </span>
                  <span style={{ fontSize: 11, color: N[700], lineHeight: 1.5 }}>
                    {d.value}
                  </span>
                </div>
              ))}
              <p style={{ fontSize: 11, color: N[500], fontStyle: 'italic', lineHeight: 1.5, marginTop: 10, marginBottom: 0 }}>
                {m.useCase}
              </p>
            </div>
            </>}
          </div>

          {/* DROITE — Interface interactive du module */}
          {active === 1 ? (
            <div style={{ gridColumn: '1 / -1' }}>
              <Module01 compact onHasResult={handleResult} />
            </div>
          ) : (
            <div style={{
              gridColumn: hasResult ? '1 / -1' : 'auto',
              borderRadius: 12,
              border: `1px solid ${m.borderColor}`,
              overflow: 'hidden',
              background: '#fff',
            }}>
              <div style={{ padding: 0 }}>
                {active === 0 && <ModuleNoteTechnique compact onHasResult={handleResult} />}
                {active === 2 && <Module02 compact onHasResult={handleResult} />}
                {active === 3 && <Module03 compact onHasResult={handleResult} />}
              </div>
            </div>
          )}
        </div>
        )}

        {/* ── Mention RGPD ─────────────────────────────────────────── */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 6, marginTop: 20, fontSize: 12, color: N[400],
        }}>
          <Lock size={13} />
          <span>Aucune donnée n&apos;est stockée ni partagée avec des tiers. Conforme RGPD.</span>
        </div>
      </div>
    </section>
  )
}
